/**
 * Annotation Toolbar
 * 
 * Left sidebar in the Annotation Editor containing:
 * - Annotation tools (click to activate, then click on canvas to place)
 * - Template selector
 * - User saved templates
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import TitleIcon from '@mui/icons-material/Title';
import ExploreIcon from '@mui/icons-material/Explore';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InventoryIcon from '@mui/icons-material/Inventory';
import NotesIcon from '@mui/icons-material/Notes';
import StraightenIcon from '@mui/icons-material/Straighten';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';

import type { AnnotationType, AnnotationTemplate } from '../../types/annotations';

interface AnnotationToolbarProps {
  activeTool: AnnotationType | null;
  onToolSelect: (tool: AnnotationType | null) => void;
  onApplyTemplate: (template: AnnotationTemplate) => void;
  templates: AnnotationTemplate[];
}

interface ToolButtonProps {
  type: AnnotationType;
  label: string;
  icon: React.ReactNode;
  description: string;
  isActive: boolean;
  onClick: () => void;
}

const ToolButton: React.FC<ToolButtonProps> = ({
  type,
  label,
  icon,
  description,
  isActive,
  onClick,
}) => (
  <Tooltip title={description} placement="right" arrow>
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        border: '2px solid transparent',
        transition: 'all 0.2s',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.1)',
          border: '2px solid rgba(255,255,255,0.2)',
        },
        '&:active': {
          backgroundColor: 'rgba(99, 102, 241, 0.2)',
        },
      }}
    >
      <Box
        sx={{
          color: 'rgba(255,255,255,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255,255,255,0.7)',
          fontWeight: 400,
          textAlign: 'center',
          fontSize: '0.7rem',
        }}
      >
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

export const AnnotationToolbar: React.FC<AnnotationToolbarProps> = ({
  activeTool,
  onToolSelect,
  onApplyTemplate,
  templates,
}) => {
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<null | HTMLElement>(null);

  const tools: Array<{
    type: AnnotationType;
    label: string;
    icon: React.ReactNode;
    description: string;
  }> = [
    {
      type: 'title-box',
      label: 'Title Box',
      icon: <TitleIcon sx={{ fontSize: 28 }} />,
      description: 'Project information and company details',
    },
    {
      type: 'north-arrow',
      label: 'North Arrow',
      icon: <ExploreIcon sx={{ fontSize: 28 }} />,
      description: 'Direction indicator showing map orientation',
    },
    {
      type: 'legend',
      label: 'Legend',
      icon: <ListAltIcon sx={{ fontSize: 28 }} />,
      description: 'Auto-populated list of plan elements',
    },
    {
      type: 'manifest',
      label: 'Manifest',
      icon: <InventoryIcon sx={{ fontSize: 28 }} />,
      description: 'Equipment quantities and materials list',
    },
    {
      type: 'notes',
      label: 'Notes',
      icon: <NotesIcon sx={{ fontSize: 28 }} />,
      description: 'Text annotations and callouts',
    },
    {
      type: 'scale-bar',
      label: 'Scale Bar',
      icon: <StraightenIcon sx={{ fontSize: 28 }} />,
      description: 'Map scale indicator',
    },
  ];

  const handleToolClick = (type: AnnotationType) => {
    // Directly add the annotation - no toggle needed
    onToolSelect(type);
  };

  return (
    <Box
      sx={{
        width: 100,
        backgroundColor: '#111827',
        borderRight: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Tools Section */}
      <Box sx={{ p: 1, flex: 1, overflowY: 'auto' }}>
        <Typography
          variant="overline"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: '0.65rem',
            letterSpacing: 1,
            display: 'block',
            textAlign: 'center',
            mb: 1,
          }}
        >
          Tools
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {tools.map((tool) => (
            <ToolButton
              key={tool.type}
              type={tool.type}
              label={tool.label}
              icon={tool.icon}
              description={tool.description}
              isActive={activeTool === tool.type}
              onClick={() => handleToolClick(tool.type)}
            />
          ))}
        </Box>

        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

        {/* Templates Section - Temporarily disabled */}
        {/* 
        <Typography
          variant="overline"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: '0.65rem',
            letterSpacing: 1,
            display: 'block',
            textAlign: 'center',
            mb: 1,
          }}
        >
          Templates
        </Typography>

        <Button
          variant="outlined"
          size="small"
          fullWidth
          startIcon={<DashboardIcon sx={{ fontSize: 16 }} />}
          endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            borderColor: 'rgba(255,255,255,0.2)',
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 1,
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.4)',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
          }}
        >
          Apply
        </Button>

        <Menu
          anchorEl={templateMenuAnchor}
          open={Boolean(templateMenuAnchor)}
          onClose={() => setTemplateMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              backgroundColor: '#1F2937',
              border: '1px solid rgba(255,255,255,0.1)',
              minWidth: 250,
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(255,255,255,0.4)',
              px: 2,
              py: 0.5,
              display: 'block',
              fontSize: '0.65rem',
            }}
          >
            Built-in Templates
          </Typography>
          {templates.filter(t => t.isBuiltIn).map((template) => (
            <MenuItem
              key={template.id}
              onClick={() => {
                onApplyTemplate(template);
                setTemplateMenuAnchor(null);
              }}
              sx={{ color: 'white' }}
            >
              <ListItemIcon>
                <DashboardIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
              </ListItemIcon>
              <ListItemText
                primary={template.name}
                secondary={template.description}
                primaryTypographyProps={{ fontSize: '0.875rem' }}
                secondaryTypographyProps={{ 
                  fontSize: '0.75rem', 
                  color: 'rgba(255,255,255,0.5)',
                  sx: { whiteSpace: 'normal' }
                }}
              />
            </MenuItem>
          ))}
          
          {templates.filter(t => !t.isBuiltIn).length > 0 && (
            <>
              <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
              <Typography
                variant="overline"
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  px: 2,
                  py: 0.5,
                  display: 'block',
                  fontSize: '0.65rem',
                }}
              >
                My Templates
              </Typography>
              {templates.filter(t => !t.isBuiltIn).map((template) => (
                <MenuItem
                  key={template.id}
                  onClick={() => {
                    onApplyTemplate(template);
                    setTemplateMenuAnchor(null);
                  }}
                  sx={{ color: 'white' }}
                >
                  <ListItemIcon>
                    <DashboardIcon sx={{ color: '#6366F1' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={template.name}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </MenuItem>
              ))}
            </>
          )}
        </Menu>
        */}
      </Box>

    </Box>
  );
};

export default AnnotationToolbar;
